import type { HelpTocItem } from "./types";

const CALLOUT_TAGS = ["note", "warning", "tip", "info"] as const;

export function slugifyHeading(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function safeUrl(raw: string): string | null {
  const url = raw.trim();
  if (!url) return null;
  if (url.startsWith("#") || url.startsWith("/")) return url;
  if (url.startsWith("mailto:")) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return null;
}

function formatInline(text: string): string {
  let output = escapeHtml(text);
  output = output.replace(/`([^`]+)`/g, "<code>$1</code>");
  output = output.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, alt: string, src: string) => {
    const url = safeUrl(src);
    if (!url) return alt;
    return `<img src="${escapeHtml(url)}" alt="${alt}" loading="lazy" />`;
  });
  output = output.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label: string, href: string) => {
    const url = safeUrl(href);
    if (!url) return label;
    const external = /^https?:\/\//i.test(url);
    const rel = external ? ' rel="noopener noreferrer" target="_blank"' : "";
    return `<a href="${escapeHtml(url)}"${rel}>${label}</a>`;
  });
  output = output.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  output = output.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  return output;
}

function headingId(used: Map<string, number>, text: string): string {
  const base = slugifyHeading(text) || "section";
  const count = used.get(base) || 0;
  used.set(base, count + 1);
  return count ? `${base}-${count + 1}` : base;
}

function parseTable(rows: string[]): string {
  const parsed = rows
    .map((row) => row.trim())
    .filter((row) => !/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(row))
    .map((row) =>
      row
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim()),
    );
  if (parsed.length < 1) return "";
  const [header, ...body] = parsed;
  const thead = `<thead><tr>${header.map((cell) => `<th>${formatInline(cell)}</th>`).join("")}</tr></thead>`;
  const tbody = `<tbody>${body
    .map((cells) => `<tr>${cells.map((cell) => `<td>${formatInline(cell)}</td>`).join("")}</tr>`)
    .join("")}</tbody>`;
  return `<div class="help-table-wrap"><table>${thead}${tbody}</table></div>`;
}

export function extractToc(markdown: string): HelpTocItem[] {
  const used = new Map<string, number>();
  const items: HelpTocItem[] = [];
  for (const line of markdown.replace(/\r\n/g, "\n").split("\n")) {
    const match = /^(#{2,3})\s+(.+)$/.exec(line.trim());
    if (!match) continue;
    const text = match[2].trim();
    items.push({
      id: headingId(used, text),
      text,
      level: match[1].length === 2 ? 2 : 3,
    });
  }
  return items;
}

export function renderHelpMarkdown(markdown: string): string {
  const source = (markdown || "").replace(/\r\n/g, "\n").trim();
  if (!source) return "";

  const lines = source.split("\n");
  const html: string[] = [];
  const usedIds = new Map<string, number>();
  let index = 0;

  const flushParagraph = (buffer: string[]) => {
    const text = buffer.join(" ").trim();
    if (text) html.push(`<p>${formatInline(text)}</p>`);
    buffer.length = 0;
  };

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    const fence = /^```(\w+)?$/.exec(trimmed);
    if (fence) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== "```") {
        code.push(lines[index]);
        index += 1;
      }
      index += 1;
      html.push(`<pre><code>${escapeHtml(code.join("\n"))}</code></pre>`);
      continue;
    }

    const callout = /^::: *(note|warning|tip|info)$/i.exec(trimmed);
    if (callout) {
      const kind = callout[1].toLowerCase();
      const body: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== ":::") {
        body.push(lines[index]);
        index += 1;
      }
      index += 1;
      html.push(
        `<aside class="help-callout help-callout-${kind}" role="note">${renderHelpMarkdown(body.join("\n"))}</aside>`,
      );
      continue;
    }

    const heading = /^(#{2,3})\s+(.+)$/.exec(trimmed);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2].trim();
      const id = headingId(usedIds, text);
      html.push(`<h${level} id="${id}"><a href="#${id}">${formatInline(text)}</a></h${level}>`);
      index += 1;
      continue;
    }

    if (trimmed === "---") {
      html.push("<hr />");
      index += 1;
      continue;
    }

    if (trimmed.startsWith("|")) {
      const rows: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith("|")) {
        rows.push(lines[index]);
        index += 1;
      }
      html.push(parseTable(rows));
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index].trim())) {
        items.push(formatInline(lines[index].trim().replace(/^[-*]\s+/, "")));
        index += 1;
      }
      html.push(`<ul>${items.map((item) => `<li>${item}</li>`).join("")}</ul>`);
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(formatInline(lines[index].trim().replace(/^\d+\.\s+/, "")));
        index += 1;
      }
      html.push(`<ol>${items.map((item) => `<li>${item}</li>`).join("")}</ol>`);
      continue;
    }

    if (trimmed.startsWith("> ")) {
      const quotes: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith("> ")) {
        quotes.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      html.push(`<blockquote>${renderHelpMarkdown(quotes.join("\n"))}</blockquote>`);
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index];
      const currentTrim = current.trim();
      if (
        !currentTrim ||
        currentTrim.startsWith("#") ||
        currentTrim.startsWith("|") ||
        currentTrim.startsWith("```") ||
        currentTrim.startsWith(":::") ||
        currentTrim.startsWith("> ") ||
        /^[-*]\s+/.test(currentTrim) ||
        /^\d+\.\s+/.test(currentTrim) ||
        currentTrim === "---"
      ) {
        break;
      }
      paragraph.push(currentTrim);
      index += 1;
    }
    flushParagraph(paragraph);
  }

  return html.join("\n");
}

export function parseVideoEmbed(url: string): { src: string; provider: "youtube" | "vimeo" } | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = parsed.pathname.replace("/", "");
      return id ? { src: `https://www.youtube-nocookie.com/embed/${id}`, provider: "youtube" } : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = parsed.searchParams.get("v");
      return id ? { src: `https://www.youtube-nocookie.com/embed/${id}`, provider: "youtube" } : null;
    }
    if (host === "vimeo.com") {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      return id ? { src: `https://player.vimeo.com/video/${id}`, provider: "vimeo" } : null;
    }
  } catch {
    return null;
  }
  return null;
}

export { CALLOUT_TAGS as HELP_CALLOUT_TAGS };
